// Helpers for the effective usable start date of a model and for clamping
// template-generated plan windows ("Last Year ...") so they never begin before
// the model's real data.
//
// Two concerns are handled here:
//
//  1. Legacy weekly models created before LEGACY_WEEKLY_START_CUTOFF have an
//     unreliable first month of weekly data (a backend bug). The Plan
//     Calculator (CreateSimulation.vue) already pushes their start to the 1st of
//     the following month via the date-picker `:min`; effectiveModelStart()
//     centralizes that rule so the "Last Year" templates honor the same bound.
//     Newer models and monthly models are returned unchanged. NOTE: the Plan
//     Optimizer intentionally does NOT apply this push-forward, so it clamps to
//     the raw model start instead of the effective one.
//
//  2. Models with less than a full year of history would otherwise get a
//     template window starting 12 months / 52 weeks before the last data point,
//     i.e. before the model's first date (out of range). clampMonthlyStart /
//     clampWeeklyStart pull the window start up to the model's (effective) start.

import moment from 'moment'

// Weekly models created strictly before this date are affected by the
// first-month backend bug.
export const LEGACY_WEEKLY_START_CUTOFF = '2025-04-09'

// Effective usable start for a model. Pushes legacy weekly models to the 1st of
// the month after their raw start; everything else passes through unchanged.
// `clientDateCreated` is a JS Date (e.g. client.date_created.toDate()).
export function effectiveModelStart (rawStart, modelFrequency, clientDateCreated) {
  if (!rawStart) return rawStart
  if (modelFrequency === 'week' && clientDateCreated && clientDateCreated < new Date(LEGACY_WEEKLY_START_CUTOFF)) {
    var parts = rawStart.split('-')
    var year = parseInt(parts[0])
    var month = parseInt(parts[1]) + 1
    if (month > 12) {
      month = 1
      year++
    }
    return year + '-' + month.toString().padStart(2, '0') + '-01'
  }
  return rawStart
}

// Clamp a monthly window start to the model's start month. Returns YYYY-MM-DD.
export function clampMonthlyStart (dateFrom, modelStart) {
  if (!modelStart) return dateFrom
  var startMonth = moment(modelStart).startOf('month')
  return moment(dateFrom).isBefore(startMonth) ? startMonth.format('YYYY-MM-DD') : dateFrom
}

// Clamp a weekly (Sunday-aligned) window start to the first Sunday on or after
// the model's start. Returns YYYY-MM-DD.
export function clampWeeklyStart (dateFrom, modelStart) {
  if (!modelStart) return dateFrom
  var lower = moment(modelStart)
  if (lower.day() !== 0) {
    lower.add(7 - lower.day(), 'days')
  }
  return moment(dateFrom).isBefore(lower) ? lower.format('YYYY-MM-DD') : dateFrom
}
